import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from "rxjs";
import {emptyServiceResponse, IServiceResponse} from "../../../../services/external-api/response-interfaces";
import {emptyMDMElement, emptyMDMElementProfile, emptyMDMHierarchy, emptyMDMHierarchyProfile, emptyMDMLevel, emptyMDMLevelProfile, emptyMDMType, emptyMDMTypeProfile, IMDMElement, IMDMHierarchy, IMDMLevel, IMDMParentElement, IMDMType} from "@retail-data-factory/rdf-angular-types-api";
import {DataService} from "../../../../services/external-api/data.service";
import {map} from "rxjs/operators";
import {Presenter} from "../../../internal/presenter/presenter";
import {ConfigurationManager} from "../../../../store/configuration-manager/configuration-manager";
import {flatMap} from "rxjs/internal/operators";

@Injectable({
  providedIn: 'root'
})
export class ApiMdmService {

  constructor(
    private dataService: DataService,
    public presenter: Presenter,
    private configurationManager: ConfigurationManager
  ) {
  }

  /**
   * Получить список типов MDM
   * @param datasourcePartyId
   */
  getTypes(datasourcePartyId?): Observable<Array<IMDMType>> {
    const param = {};
    if (datasourcePartyId){
      param['datasourcepartyid'] = datasourcePartyId;
    }
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/types`, param).pipe(
      map(result => {
        const types: Array<IMDMType> = result.report.map(item => this.mapTypeFromService(item));
        return types;
      })
    );
  }

  getType(typeCode: string): Observable<IMDMType> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          return this.mapTypeFromService(result.report[0]);
        } else {
          return {...emptyMDMType};
        }
      })
    );
  }


  createType(type: IMDMType): Observable<IServiceResponse> {
    const body = {
      code: type.code,
      name: type.name,
      description: type.description,
      isDefault: type.isDefault,
      profile: type.profile
    };
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiDict')}/types`, {}, body);

  }

  updateType(type: IMDMType): Observable<IServiceResponse> {
    const body = {
      name: type.name,
      description: type.description,
      isDefault: type.isDefault,
      profile: type.profile
    };
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiDict')}/types/${type.code}`, {}, body);
  }

  deleteType(): Observable<IServiceResponse> {
    return of(emptyServiceResponse);

  }

  getHierarchyList(typeCode: string, dataSourcePartyId = ''): Observable<Array<IMDMHierarchy>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/hierarchy`, {datasourcepartyid: dataSourcePartyId}).pipe(
      map(result => {
        const hierList: Array<IMDMHierarchy> = result.report.map(item => this.mapHierarchyFromService(item));
        return hierList;
      })
    );

  }

  getMdmDefaultHierarchy(typeCode: string, dataSourcePartyId = ''): Observable<IMDMHierarchy> {

    return this.getHierarchyList(typeCode, dataSourcePartyId).pipe(
      map((result) => {
        const hier: IMDMHierarchy = result[0];
        return hier;
      }),
      flatMap((result: IMDMHierarchy) => {
        return this.getHierarchyLevels(typeCode, result.id, dataSourcePartyId).pipe(
          map(resultLevels => {
            result.levels = resultLevels;
            return result;
          })
        );
      }),
      flatMap((result: IMDMHierarchy) => {
        const list = [];

        return this.getHierarchyElements(typeCode, result.id,result.levels.length.toString(), 0, 10000, '',dataSourcePartyId).pipe(
          map(resultElements=>{
            resultElements.report.forEach((item,idx)=>{
              for (let i= 1; i<=result.levels.length;i++) {
                const elem = this.mapElementFromService(item, i);
                if (idx === 0 && elem.code) {
                  result.levels[i-1].elements.push(elem);
                }else {
                  if (idx>0 && elem.code && elem.code!==this.mapElementFromService(resultElements.report[idx-1],i).code) {
                    result.levels[i-1].elements.push(elem);
                  }

                }
              }
            });
            return result;
          })
        );


        // result.levels.forEach(t => list.push(this.getHierarchyDetail(typeCode, result.id, t.id, 0, 10000, '', dataSourcePartyId)));
        // return forkJoin(list).pipe(
        //   map(resultFork => {
        //
        //     resultFork.forEach((t, idx) => {
        //
        //     });
        //     return result;
        //
        //   })
        // );

      })
    );


    return of({...emptyMDMHierarchy});
  }

  createHierarchy(hierarchy: IMDMHierarchy): Observable<IServiceResponse> {
    const body = {
      code: hierarchy.code,
      name: hierarchy.name,
      description: hierarchy.description,
      is_default: hierarchy.isDefault ? '1' : '0',
      profile: {...emptyMDMHierarchyProfile, ...hierarchy.profile}
    };
    if (typeof body.profile.param['lowest_level'] === 'string') {
      body.profile.param.lowest_level = parseInt(body.profile.param.lowest_level, 10);
    }
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiDict')}/types/${hierarchy.type.code}/hierarchy`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  updateHierarchy(hierarchy: IMDMHierarchy): Observable<IServiceResponse> {

    const body = {
      code: hierarchy.code,
      name: hierarchy.name,
      description: hierarchy.description,
      is_default: hierarchy.isDefault ? '1' : '0',
      profile: {...emptyMDMHierarchyProfile, ...hierarchy.profile}
    };
    if (typeof body.profile.param['lowest_level'] === 'string') {
      body.profile.param.lowest_level = parseInt(body.profile.param.lowest_level, 10);
    }
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiDict')}/types/${hierarchy.type.code}/hierarchy/${hierarchy.id}`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  deleteHierarchy(hierarchy: IMDMHierarchy): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiDict')}/types/${hierarchy.type.code}/hierarchy/${hierarchy.id}`, {}).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }


  getHierarchyLevels(typeCode: string, hierCode: string, dataSourcePartyId = ''): Observable<Array<IMDMLevel>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/hierarchy/${hierCode}/levels`, {datasourcepartyid: dataSourcePartyId}).pipe(
      map(result => {
        const levels: Array<IMDMLevel> = result.report.map(item => this.mapLevelFromService(item));
        return levels;
      })
    );
  }

  getHierarchyLevel(typeCode: string, hierCode: string, levelCode: string): Observable<IMDMLevel> {
    return of({...emptyMDMLevel});
  }

  createHierarchyLevel(typeCode: string, hierarchyId: string, level: IMDMLevel): Observable<IServiceResponse> {
    const body = {
      code: level.code,
      name: level.name,
      description: level.description,
      profile: level.profile
    };
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/hierarchy/${hierarchyId}/levels`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  updateHierarchyLevel(typeCode: string, hierarchyId: string, level: IMDMLevel): Observable<IServiceResponse> {
    const body = {
      code: level.code,
      name: level.name,
      description: level.description,
      profile: level.profile
    };
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/hierarchy/${hierarchyId}/levels/${level.id}`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  deleteHierarchyLevel(typeCode: string, hierarchyId: string, levelId: string): Observable<IServiceResponse> {

    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/hierarchy/${hierarchyId}/levels/${levelId}`, {}).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  getHierarchyElements(typeCode: string, hierCode: string, levelCode: string, offset = 0, limit = 0, searchStr?: string, dataSourcePartyId = ''): Observable<IServiceResponse> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/hierarchy/${hierCode}/levels/${levelCode}/elements?offset=${offset}&limit=${limit}`, {datasourcepartyid: dataSourcePartyId});
  }


  getHierarchyDetail(typeCode: string, hierCode: string, levelCode: string, offset = 0, limit = 0, searchStr?: string, dataSourcePartyId = ''): Observable<Array<IMDMElement>> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/hierarchy/${hierCode}/levels/${levelCode}/elements?offset=${offset}&limit=${limit}`, {datasourcepartyid: dataSourcePartyId}).pipe(
      map(result => {
        const elements: Array<IMDMElement> = result.report.map(item => this.mapLastLevelElementFromService(item));
        return elements;
      })
    );

  }

  getMdmElement(typeCode: string, hierCode: string, levelCode: string, elementId: string): Observable<IMDMElement> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/hierarchy/${hierCode}/levels/${levelCode}/elements/${elementId}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          const element: IMDMElement = this.mapLastLevelElementFromService(result.report[0]);
          return element;
        } else {
          return {...emptyMDMElement};
        }
      })
    );
  }

  getMdmElementById(elementId: string): Observable<IMDMElement> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/elements/${elementId}`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          const item = result.report[0];
          const element: IMDMElement = {
            ...emptyMDMElement,
            partyId: item['id_prty'].toString(),
            id: item['id'].toString(),
            type: item['type'],
            code: item['code'],
            name: item['name'],
            description: item['desc'],
            parentId: '',
            parentCode: '',
            parentName: '',
            parents: [],
            profile: item['profile']
          };
          return element;
        } else {
          return {...emptyMDMElement};
        }
      })
    );
  }


  getMdmImage(elementId: string): Observable<string> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/elements/${elementId}/images`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          return result.report[0]['image'] ? result.report[0]['image'] : '';
        } else {
          return '';
        }
      })
    );
  }

  saveMdmImage(elementId: string, image: string): Observable<IServiceResponse> {
    const body = image;
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiDict')}/elements/${elementId}/images`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  getMdmElementByCode(typeCode: string, hierCode: string, levelCode: string, elementCode: string): Observable<IMDMElement> {
    const encodedElementCode = encodeURIComponent(elementCode);
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/hierarchy/${hierCode}/levels/${levelCode}/elements/${encodedElementCode}?query_field=code`, {}).pipe(
      map(result => {
        if (result.report.length > 0) {
          const element: IMDMElement = this.mapLastLevelElementFromService(result.report[0]);
          return element;
        } else {
          return {...emptyMDMElement};
        }
      })
    );
  }


  createMdmElement(typeCode: string, functionId: string, parentLevelId: string, element: IMDMElement): Observable<IServiceResponse> {
    const body = this.mapElementToService(typeCode, element);
    body['parent_level_id'] = parentLevelId;
    body['function_id'] = functionId;
    return this.dataService.postData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/elements/`, {}, body).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  updateMdmElement(typeCode: string, element: IMDMElement): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/elements/${element.id}`, {}, this.mapElementToService(typeCode, element)).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }

  deleteMdmElement(typeCode: string, element: IMDMElement): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl('apiDict')}/types/${typeCode}/elements/${element.id}`, {}).pipe(
      map((result: IServiceResponse) => {
        return result;
      })
    );
  }


  private mapTypeFromService(item: object): IMDMType {
    return {
      ...emptyMDMType,
      ownerPartyId: item['party_id'],
      code: item['type_code'],
      name: item['type_name'],
      description: item['type_description'],
      isDefault: item['is_default'] === 1,
      defaultHierarchyLevelCount: item['default_hierarchy_level_count']?item['default_hierarchy_level_count']:0,
      defaultHierarchyName:item['default_hierarchy_name']?item['default_hierarchy_name']:'',
      profile: {...emptyMDMTypeProfile, ...item['profile']}
    };
  }

  private mapHierarchyFromService(item: object): IMDMHierarchy {
    const result: IMDMHierarchy = {
      ...emptyMDMHierarchy,
      id: item['hier_id'],
      code: item['hier_code'],
      name: item['hier_name'],
      description: item['hier_description'],
      type: this.presenter.getMdmType(item['type_code']),
      isDefault: item['is_default'] === 1,
      levels: [],
      profile: {...emptyMDMHierarchyProfile, ...item['profile']}

    };
    if (typeof result.profile.param['lowest_level'] === 'string') {
      result.profile.param.lowest_level = parseInt(result.profile.param.lowest_level, 10);
    }
    return result;
  }

  private mapLevelFromService(item: object): IMDMLevel {
    return {
      ...emptyMDMLevel,
      id: item['level_id'] !== null && item['level_id'] !== undefined ? item['level_id'].toString() : '',
      levelNum: item['level_num'] !== null && item['level_num'] !== undefined ? item['level_num'].toString() : '',
      levelParentId: item['level_parent_id'] !== null && item['level_parent_id'] !== undefined ? item['level_parent_id'].toString() : '',
      code: item['level_code'],
      name: item['level_name'],
      description: item['level_description'],
      elements: [],
      profile: {...emptyMDMLevelProfile, ...item['profile']},
    };
  }

  private mapElementFromService(item: object, levelNum: number): IMDMElement {
    const maxLevelNum: number = item['max_level_num'];
    if (levelNum > maxLevelNum) {
      return {
        ...emptyMDMElement
      };
    } else {
      return {
        ...emptyMDMElement,
        id: item[`level_${levelNum}_id`] ? item[`level_${levelNum}_id`].toString() : item[`level_${levelNum}_code`],
        code: item[`level_${levelNum}_code`],
        name: item[`level_${levelNum}_name`],
        description: item[`level_${levelNum}_desc`],
        parentId: levelNum > 1 ? item[`level_${levelNum - 1}_id`] : '',
        parentCode: levelNum > 1 ? item[`level_${levelNum - 1}_code`] : '',
        parentName: levelNum > 1 ? item[`level_${levelNum - 1}_name`] : '',
        parents: levelNum > 1 ? this.getParentsFromItem(item, levelNum - 1) : [],
        profile: {...emptyMDMElementProfile, ...item[`level_${levelNum}_profile`]}
      };
    }
  }

  private mapLastLevelElementFromService(item: object): IMDMElement {
    const maxLevelNum: number = item['max_level_num'];
    return this.mapElementFromService(item, maxLevelNum);
  }


  getParentsFromItem(item, level: number): Array<IMDMParentElement> {
    const result: Array<IMDMParentElement> = [];
    if (level === 0) return result;
    for (let i = 1; i <= level; i++) {
      result.push(
        {
          code: item[`level_${i}_code`],
          name: item[`level_${i}_name`]
        }
      );
    }
    return result;
  }

  mapElementToService(typeCode: string, element: IMDMElement): object {
    const result = {
      code: element.code,
      name: element.name,
      parent_code: element.parentCode,
      parent_id: element.parentId,
      description: element.description,
      profile: element.profile ? element.profile : {}
    };
    return result;
  }

  getResourceCodeFromMdmType(mdmTypeCode: string) {
    switch (mdmTypeCode) {
      case 'RED_NOMENCLATURE':
        return 'MDM_NOMENCLATURE_SHARED';
      case 'RED_ORGANISATION':
        return 'MDM_ORGANISATION_SHARED';
      case 'RED_CHANNEL':
        return 'MDM_CHANNEL_SHARED';
    }
    return '';
  }

}
